import {
    AmcScanField,
    AmountField,
    BooleanField,
    CheckboxField,
    CreditAuthorizationField,
    DateField,
    EventCell,
    EyeIcon,
    PatientInactiveBadge,
    PatientStatusBadge,
    SelectField,
    SubcategoryField,
    TextField,
} from "./AdministrationFields";
import { ChronicBillingCell } from "./ChronicBillingCell";
import { GuardianContactField } from "./GuardianContact";
import { RobotPdaHistoryPanel } from "./HistoryModals";
import { PatientDocumentPanel } from "./PatientDocuments";
import { AMO_OPTIONS, BPM_LABELS, PATIENT_STATUS_OPTIONS } from "../model/administrationConstants";
import {
    formatDate,
    formatPatientName,
    isAmcDateEnabled,
    isAutomaticDebitAuthorizationEnabled,
    normalizeCreditAuthorization,
    normalizePatientStatus,
} from "../model/administrationRules";
import {
    iconButtonStyle,
    pdaHistoryActionsStyle,
    pdaHistoryButtonStyle,
    pdaRemarkWithHistoryStyle,
    primaryButtonStyle,
    secondaryButtonStyle,
} from "../styles/administrationModalStyles";
import {
    administrationFieldsRowStyle,
    administrationRowsStyle,
    automaticDebitBlockStyle,
    inputStyle,
    patientDetailStyle,
    patientHeaderControlsStyle,
    patientHeaderFieldStyle,
    patientIdentityActionsStyle,
    patientNameWithBadgeStyle,
    verticalActionsStyle,
    verticalDetailHeaderStyle,
    verticalDetailStyle,
    verticalFieldLabelStyle,
    verticalFieldStyle,
    verticalFieldsGridStyle,
    verticalPatientIdentityStyle,
    verticalSectionStyle,
    verticalSectionTitleStyle,
    verticalSectionsStyle,
} from "../styles/administrationStyles";

function renderField(label, control, key = label) {
    return (
        <label key={key} style={verticalFieldStyle}>
            <span style={verticalFieldLabelStyle}>{label}</span>
            {control}
        </label>
    );
}

// Identité, statut et actions de la fiche.
function VerticalPatientHeader({ current, dirty, saving, onPatch, onSave, onCancel, onHistory, onIdentity, onDocuments, productionSubcategories }) {
    return (
            <div style={verticalDetailHeaderStyle}>
                <div style={verticalPatientIdentityStyle}>
                    <div style={{ minWidth: 0 }}>
                        <span style={patientNameWithBadgeStyle}>
                            <strong style={{ color: "#111827", fontSize: "18px", overflowWrap: "anywhere" }}>
                                {formatPatientName(current)}
                            </strong>
                            <PatientStatusBadge status={current.patientStatus} />
                            <PatientInactiveBadge inactive={current.inactive} />
                        </span>
                        <span style={patientDetailStyle}>Né(e) le {formatDate(current.dateNaissance)}</span>
                        <span style={patientDetailStyle}>Chambre {current.chambre || "—"}</span>
                        <span style={patientDetailStyle}>NIR {current.nir || "—"}</span>
                        {current.productionSubcategory && <span style={patientDetailStyle}>Sous-catégorie {current.productionSubcategory}</span>}
                        <div style={patientHeaderControlsStyle}>
                            <label style={patientHeaderFieldStyle}>
                                <span>Statut patient</span>
                                <SelectField
                                    value={current.patientStatus}
                                    options={PATIENT_STATUS_OPTIONS}
                                    onChange={value => onPatch({ patientStatus: normalizePatientStatus(value) })}
                                />
                            </label>
                            <label style={patientHeaderFieldStyle}>
                                <span>État patient</span>
                                <select
                                    value={current.inactive ? "inactive" : "active"}
                                    onChange={event => onPatch({ inactive: event.target.value === "inactive" })}
                                    style={{
                                        ...inputStyle,
                                        borderColor: current.inactive ? "#60a5fa" : "#cbd5e1",
                                        background: current.inactive ? "#eff6ff" : "white",
                                        color: current.inactive ? "#1d4ed8" : "#334155",
                                        fontWeight: 800,
                                    }}
                                >
                                    <option value="active">Actif</option>
                                    <option value="inactive">Inactif</option>
                                </select>
                            </label>
                            <label style={patientHeaderFieldStyle}>
                                <span>Sous-groupe patient</span>
                                <SubcategoryField
                                    value={current.productionSubcategory || ""}
                                    options={productionSubcategories}
                                    onChange={value => onPatch({ productionSubcategory: value || null })}
                                />
                            </label>
                        </div>
                    </div>
                    <div style={patientIdentityActionsStyle}>
                        <button
                            type="button"
                            aria-label={`Modifier l'identité de ${formatPatientName(current)}`}
                            title="Modifier l'identité"
                            onClick={onIdentity}
                            style={iconButtonStyle}
                        >
                            ✎
                        </button>
                        <button
                            type="button"
                            aria-label={`Voir les documents de ${formatPatientName(current)}`}
                            title="Voir les documents"
                            onClick={onDocuments}
                            style={iconButtonStyle}
                        >
                            <EyeIcon />
                        </button>
                    </div>
                </div>
                <div style={verticalActionsStyle}>
                    {dirty ? (
                        <>
                            <button type="button" disabled={saving} onClick={onSave} style={primaryButtonStyle}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
                            <button type="button" disabled={saving} onClick={onCancel} style={secondaryButtonStyle}>Annuler</button>
                        </>
                    ) : (
                        <button type="button" onClick={onHistory} style={secondaryButtonStyle}>Historique</button>
                    )}
                </div>
            </div>
    );
}

// Autorisation de crédit, AMO/AMC, tuteur et prélèvement automatique.
function AdministrationSection({ row, current, onPatch, onUploadDocument, onOpenDocument, onDeleteDocument, documentBusy }) {
    return (
            <section style={verticalSectionStyle("administration")}>
                <h2 style={verticalSectionTitleStyle("administration")}>Administration</h2>
                <div style={administrationRowsStyle}>
                    <div style={administrationFieldsRowStyle(3)}>
                        {renderField("Autorisation crédit", (
                            <CreditAuthorizationField
                                row={current}
                                onChange={value => onPatch({
                                    creditAuthorization: value,
                                    creditAuthorizationType: null,
                                    creditAuthorizationAmount: null,
                                })}
                            />
                        ))}
                        {renderField("Montant autorisation crédit", (
                            <AmountField
                                label="Montant autorisation crédit"
                                value={current.creditAuthorizationAmount}
                                disabled={normalizeCreditAuthorization(current) !== "Oui"}
                                onChange={value => onPatch({ creditAuthorizationAmount: value })}
                            />
                        ))}
                        {renderField("Crédit", <TextField value={current.creditNote} onChange={value => onPatch({ creditNote: value })} multiline />)}
                    </div>
                    <div style={administrationFieldsRowStyle(3)}>
                        {renderField("AMO", <SelectField value={current.amoStatus} options={AMO_OPTIONS} onChange={value => onPatch({ amoStatus: value })} />)}
                        {renderField("Scan AMC", (
                            <AmcScanField
                                value={current.amcScanReceived}
                                onChange={value => onPatch({ amcScanReceived: value, ...(!isAmcDateEnabled(value) ? { amcDate: null } : {}) })}
                            />
                        ))}
                        {renderField("AMC", (
                            <DateField
                                value={current.amcDate}
                                disabled={!isAmcDateEnabled(current.amcScanReceived)}
                                onChange={value => onPatch({ amcDate: value })}
                            />
                        ))}
                    </div>
                    <div style={administrationFieldsRowStyle(2)}>
                        {renderField("Contact tuteur", (
                            <GuardianContactField
                                status={current.guardianContactStatus}
                                contact={current.guardianContactValue}
                                onPatch={onPatch}
                            />
                        ))}
                        {renderField("Date d'envoi", <DateField value={current.guardianSentDate} onChange={value => onPatch({ guardianSentDate: value })} />)}
                    </div>
                    <div style={administrationFieldsRowStyle(2)}>
                        {renderField("Déclaration pharmacien", <TextField value={current.correspondingPharmacistDeclaration} onChange={value => onPatch({ correspondingPharmacistDeclaration: value })} multiline />)}
                        {renderField("Remarques administratives", <TextField value={current.administrativeRemarks} onChange={value => onPatch({ administrativeRemarks: value })} multiline />)}
                    </div>
                    <div style={administrationFieldsRowStyle(1)}>
                        {renderField("Autorisation de prélèvement automatique", (
                            <div style={automaticDebitBlockStyle}>
                                <CheckboxField
                                    label="Autorisation de prélèvement automatique"
                                    checked={isAutomaticDebitAuthorizationEnabled(current.scanAuto)}
                                    onChange={checked => onPatch({ scanAuto: checked ? "Oui" : "Non" })}
                                />
                                <PatientDocumentPanel
                                    documents={current.documents || []}
                                    busy={documentBusy}
                                    uploadEnabled={isAutomaticDebitAuthorizationEnabled(current.scanAuto)}
                                    onUpload={onUploadDocument}
                                    onOpen={onOpenDocument}
                                    onDelete={onDeleteDocument}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </section>
    );
}

// Facturation admission, BPM et remarques de production PDA.
function AdmissionBpmPdaSections({ row, current, saving, onPatch, onEvent, onRobotHistory, onArchivePdaComment, pdaHistoryRefreshKey }) {
    return (
        <>
                <section style={verticalSectionStyle("admission")}>
                    <h2 style={verticalSectionTitleStyle("admission")}>Facturation admission</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Bilan", <DateField value={current.admissionAssessmentDate} onChange={value => onPatch({ admissionAssessmentDate: value })} />)}
                        {renderField("Entretien", <DateField value={current.admissionInterviewDate} onChange={value => onPatch({ admissionInterviewDate: value })} />)}
                        {renderField("Commentaire entretien", <TextField value={current.admissionInterviewNote} onChange={value => onPatch({ admissionInterviewNote: value })} multiline />)}
                        {renderField("Adhésion", <DateField value={current.admissionMembershipDate} onChange={value => onPatch({ admissionMembershipDate: value })} />)}
                        {renderField("Vaccin grippe", <EventCell row={row} type="FLU" vaccination onOpen={onEvent} />)}
                        {renderField("Vaccin Covid", <EventCell row={row} type="COVID" vaccination onOpen={onEvent} />)}
                    </div>
                </section>

                <section style={verticalSectionStyle("bpm")}>
                    <h2 style={verticalSectionTitleStyle("bpm")}>BPM</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField(BPM_LABELS.bpmInitialInterview, <TextField value={current.bpmInitialInterview} onChange={value => onPatch({ bpmInitialInterview: value })} multiline />)}
                        {renderField(BPM_LABELS.bpmYear1TreatmentAnalysis, <TextField value={current.bpmYear1TreatmentAnalysis} onChange={value => onPatch({ bpmYear1TreatmentAnalysis: value })} multiline />)}
                        {renderField(BPM_LABELS.bpmYear1FollowUpInterview, <TextField value={current.bpmYear1FollowUpInterview} onChange={value => onPatch({ bpmYear1FollowUpInterview: value })} multiline />)}
                        {renderField(BPM_LABELS.bpmYear1ObservanceInterview, <TextField value={current.bpmYear1ObservanceInterview} onChange={value => onPatch({ bpmYear1ObservanceInterview: value })} multiline />)}
                    </div>
                </section>

                <section style={verticalSectionStyle("pda")}>
                    <h2 style={verticalSectionTitleStyle("pda")}>PDA</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Remarque production", (
                            <div style={pdaRemarkWithHistoryStyle}>
                                <TextField label="Remarque production" value={current.pdaProductionRemark} onChange={value => onPatch({ pdaProductionRemark: value })} multiline />
                                <div style={pdaHistoryActionsStyle}>
                                    <button
                                        type="button"
                                        onClick={() => onArchivePdaComment(current)}
                                        disabled={!String(current.pdaProductionRemark || "").trim() || saving}
                                        style={{
                                            ...pdaHistoryButtonStyle,
                                            opacity: !String(current.pdaProductionRemark || "").trim() || saving ? 0.55 : 1,
                                            cursor: !String(current.pdaProductionRemark || "").trim() || saving ? "not-allowed" : "pointer",
                                        }}
                                    >
                                        Ajouter le commentaire à l'historique
                                    </button>
                                    <button type="button" onClick={onRobotHistory} style={pdaHistoryButtonStyle}>Voir tous les commentaires PDA</button>
                                </div>
                                <RobotPdaHistoryPanel row={row} refreshKey={pdaHistoryRefreshKey} />
                            </div>
                        ))}
                        {renderField("Antalgique", <TextField value={current.antalgique} onChange={value => onPatch({ antalgique: value })} multiline />)}
                    </div>
                </section>
        </>
    );
}

// Insuline, Zopiclone, produits chers, facturation chronique et remarques de facturation.
function BillingSections({ row, current, onPatch, onEvent, onOpenChronicProducts, onOpenBillingHistory, onRefreshBilling, user }) {
    return (
        <>
                <section style={verticalSectionStyle("insulin")}>
                    <h2 style={verticalSectionTitleStyle("insulin")}>Insuline</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Oui / Non", <BooleanField value={current.insulin} onChange={value => onPatch({ insulin: value })} />)}
                        {renderField("Nb factures autorisé", <TextField value={current.authorizedInvoiceCount} onChange={value => onPatch({ authorizedInvoiceCount: value })} />)}
                        {renderField("Dates facturation", <EventCell row={row} type="INSULIN" onOpen={onEvent} />)}
                    </div>
                </section>

                <section style={verticalSectionStyle("zopiclone")}>
                    <h2 style={verticalSectionTitleStyle("zopiclone")}>Zopiclone</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Oui / Non", <BooleanField value={current.zopiclone} onChange={value => onPatch({ zopiclone: value })} />)}
                        {renderField("Dernière facturation", <EventCell row={row} type="ZOPICLONE" onOpen={onEvent} />)}
                    </div>
                </section>

                <section style={verticalSectionStyle("expensiveProducts")}>
                    <h2 style={verticalSectionTitleStyle("expensiveProducts")}>Produits chers</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Produit(s)", <TextField value={current.expensiveProducts} onChange={value => onPatch({ expensiveProducts: value })} multiline />)}
                        {renderField("Dates facturation", <EventCell row={row} type="EXPENSIVE_PRODUCT" onOpen={onEvent} />)}
                    </div>
                </section>

                <section style={verticalSectionStyle("chronic")}>
                    <h2 style={verticalSectionTitleStyle("chronic")}>Facturation chronique</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Date de dernière facturation", (
                            <div style={{ display: "grid", gap: "10px" }}>
                                <DateField
                                    value={current.chronicLastBillingDate}
                                    label="Dernière facturation chronique"
                                    onChange={value => onPatch({ chronicLastBillingDate: value })}
                                />
                                <ChronicBillingCell
                                    row={current}
                                    user={user}
                                    onOpenProducts={onOpenChronicProducts}
                                    onOpenHistory={onOpenBillingHistory}
                                    onChanged={onRefreshBilling}
                                />
                            </div>
                        ))}
                    </div>
                </section>

                <section style={verticalSectionStyle("billingRemarks")}>
                    <h2 style={verticalSectionTitleStyle("billingRemarks")}>Facturation</h2>
                    <div style={verticalFieldsGridStyle}>
                        {renderField("Remarque facturation / MTE", <TextField value={current.billingRemarks} onChange={value => onPatch({ billingRemarks: value })} multiline />)}
                        {renderField("À facturer", <TextField value={current.toInvoice} onChange={value => onPatch({ toInvoice: value })} multiline />)}
                    </div>
                </section>
        </>
    );
}

export function PatientVerticalView({
    row,
    current,
    dirty,
    saving,
    onPatch,
    onSave,
    onCancel,
    onHistory,
    onIdentity,
    onDocuments,
    onEvent,
    onOpenChronicProducts,
    onOpenBillingHistory,
    onRefreshBilling,
    user,
    onUploadDocument,
    onOpenDocument,
    onDeleteDocument,
    documentBusy,
    onRobotHistory,
    onArchivePdaComment,
    pdaHistoryRefreshKey,
    productionSubcategories = [],
}) {
    return (
        <div style={verticalDetailStyle}>
            <VerticalPatientHeader current={current} dirty={dirty} saving={saving} onPatch={onPatch} onSave={onSave} onCancel={onCancel} onHistory={onHistory} onIdentity={onIdentity} onDocuments={onDocuments} productionSubcategories={productionSubcategories} />

            <div style={verticalSectionsStyle}>
                <AdministrationSection row={row} current={current} onPatch={onPatch} onUploadDocument={onUploadDocument} onOpenDocument={onOpenDocument} onDeleteDocument={onDeleteDocument} documentBusy={documentBusy} />

                <AdmissionBpmPdaSections row={row} current={current} saving={saving} onPatch={onPatch} onEvent={onEvent} onRobotHistory={onRobotHistory} onArchivePdaComment={onArchivePdaComment} pdaHistoryRefreshKey={pdaHistoryRefreshKey} />

                <BillingSections row={row} current={current} onPatch={onPatch} onEvent={onEvent} onOpenChronicProducts={onOpenChronicProducts} onOpenBillingHistory={onOpenBillingHistory} onRefreshBilling={onRefreshBilling} user={user} />

            </div>
        </div>
    );
}
